import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  Filter,
  FileCheck,
  TrendingUp,
  Users,
} from 'lucide-react';
import { reportService } from '@/services/reportService';
import { adminService, Hospital } from '@/services/adminService';
import { useAuth } from '@/contexts/AuthContext';
import { UserLevel } from '@/types/auth.types';
import { VonComplianceReport as VonComplianceReportType } from '@/types/report.types';
import { useToast } from '@/hooks/use-toast';

export const VonComplianceReport: React.FC = () => {
  const { utilizator } = useAuth();
  const isAdmin = utilizator?.user_level_id === UserLevel.ADMINISTRATOR;
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<VonComplianceReportType | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hospitalId, setHospitalId] = useState<string>(
    isAdmin ? 'all' : (utilizator?.hospital_id?.toString() || '')
  );
  const [hospitals, setHospitals] = useState<Hospital[]>([]);

  // Load hospitals for admin users
  useEffect(() => {
    const loadHospitals = async () => {
      if (isAdmin) {
        try {
          const hospitalList = await adminService.listHospitals();
          setHospitals(hospitalList);
        } catch (error) {
          console.error('Failed to load hospitals:', error);
          toast({
            title: 'Eroare',
            description: 'Nu s-au putut încărca spitalele',
            variant: 'destructive',
          });
        }
      }
    };
    loadHospitals();
  }, [isAdmin, toast]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      if (hospitalId && hospitalId !== 'all') {
        filters.hospital_id = parseInt(hospitalId);
      }

      const response = await reportService.getVonComplianceReport(filters);

      // Backend already returns the correct structure
      setData(response);
    } catch (error) {
      console.error('Error fetching VON compliance:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele raportului RONN',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getComplianceStatus = (rate: number) => {
    if (rate >= 95) return { color: 'text-green-600', badge: 'success', icon: CheckCircle2 };
    if (rate >= 80) return { color: 'text-yellow-600', badge: 'warning', icon: AlertCircle };
    return { color: 'text-red-600', badge: 'destructive', icon: XCircle };
  };

  const getComplianceColor = (rate: number) => {
    if (rate >= 95) return 'bg-green-500';
    if (rate >= 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtre raport
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="start-date">Data început</Label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="end-date">Data sfârșit</Label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="hospital">Spital</Label>
              <Select
                value={hospitalId}
                onValueChange={setHospitalId}
                disabled={!isAdmin}
              >
                <SelectTrigger id="hospital">
                  <SelectValue placeholder="Selectează spital" />
                </SelectTrigger>
                <SelectContent>
                  {isAdmin && <SelectItem value="all">Toate spitalele</SelectItem>}
                  {hospitals.map((hospital) => (
                    <SelectItem key={hospital.id} value={hospital.id.toString()}>
                      {hospital.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={fetchData} className="w-full">
                <Filter className="h-4 w-4 mr-2" />
                Aplică filtre
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                  <p className="text-3xl font-bold text-blue-600">
                    {data.summary?.total_eligible_patients ?? 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Pacienți eligibili</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <FileCheck className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                  <p className="text-3xl font-bold text-purple-600">
                    {data.summary?.von_eligible_patients ?? 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Eligibili RONN</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="text-3xl font-bold text-green-600">
                    {data.summary?.submission_ready ?? 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Gata pentru trimitere</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                  <p className="text-3xl font-bold text-orange-600">
                    {data.summary?.overall_compliance?.toFixed(1) ?? '0.0'}%
                  </p>
                  <p className="text-sm text-muted-foreground">Conformitate generală</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Pregătire pentru trimitere</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Gata</p>
                      <p className="text-2xl font-bold text-green-600">
                        {data.submission_readiness?.ready_count ?? 0}
                      </p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Necesită revizuire</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {data.submission_readiness?.needs_review ?? 0}
                      </p>
                    </div>
                    <AlertCircle className="h-8 w-8 text-yellow-600" />
                  </div>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Incomplete</p>
                      <p className="text-2xl font-bold text-red-600">
                        {data.submission_readiness?.incomplete ?? 0}
                      </p>
                    </div>
                    <XCircle className="h-8 w-8 text-red-600" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Conformitate pe secțiuni RONN</CardTitle>
            </CardHeader>
            <CardContent>
              {data.section_compliance && data.section_compliance.length > 0 ? (
                <div className="space-y-4">
                  {data.section_compliance.map((section, index) => {
                    const status = getComplianceStatus(section.completion_rate);
                    const StatusIcon = status.icon;
                    return (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold">{section.name}</h4>
                              <Badge variant={status.badge as any}>
                                {section.completion_rate?.toFixed(1) ?? '0.0'}%
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">Secțiune: {section.section}</p>
                          </div>
                          <StatusIcon className={`h-6 w-6 ${status.color}`} />
                        </div>
                        <Progress
                          value={section.completion_rate}
                          className="mb-2"
                        />
                        <div className="mt-2 flex gap-4 text-sm text-muted-foreground">
                          <span>Complet: {section.patients_completed ?? 0}</span>
                          <span>Lipsă: {section.patients_missing ?? 0}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Nu există date disponibile pentru secțiunile RONN
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {data.data_quality_issues && data.data_quality_issues.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  Probleme de calitate a datelor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cod pacient</TableHead>
                        <TableHead>Nume</TableHead>
                        <TableHead>Vârsta gestațională</TableHead>
                        <TableHead>Completitudine</TableHead>
                        <TableHead>Secțiuni lipsă</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.data_quality_issues.map((issue, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{issue.patient_code}</TableCell>
                          <TableCell>{issue.name}</TableCell>
                          <TableCell>{issue.gestational_age} săptămâni</TableCell>
                          <TableCell>
                            <Badge variant={issue.completeness >= 80 ? 'default' : 'destructive'}>
                              {issue.completeness?.toFixed(1) ?? '0.0'}%
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {issue.missing_sections?.slice(0, 3).map((section, idx) => (
                                <div key={idx}>
                                  <Badge variant="outline" className="text-xs">
                                    {section}
                                  </Badge>
                                </div>
                              ))}
                              {issue.missing_sections && issue.missing_sections.length > 3 && (
                                <span className="text-xs text-muted-foreground">
                                  +{issue.missing_sections.length - 3} mai multe
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
